/**
 * API Keys Table Component
 *
 * Displays a table of API keys with their information, status badges, and actions.
 * Supports loading states, empty states, error handling, and row selection.
 */

import { useState } from 'react';
import {
  AlertCircle,
  Loader2,
  MoreVertical,
  Trash2,
  RefreshCw,
  Settings2,
  Eye,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiKeyDeleteDialog } from './api-key-delete-dialog';
import { ApiKeyRotateDialog } from './api-key-rotate-dialog';
import { ApiKeyRateLimitsDialog } from './api-key-rate-limits-dialog';
import type { ApiKey, ClientType } from '@/types/api-key';

interface ApiKeysTableProps {
  apiKeys: ApiKey[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onKeyClick: (apiKey: ApiKey) => void;
  selectedKeys: string[];
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Format Date
 * Formats an ISO date string to a readable format
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format Rate Limit
 * Formats rate limit as readable string
 */
function formatRateLimit(maxRequests: number, windowMs: number): string {
  const windowMinutes = Math.round(windowMs / 60000);
  return `${maxRequests.toLocaleString()} / ${windowMinutes} min`;
}

/**
 * Get Client Type Badge Variant
 */
function getClientTypeBadgeVariant(clientType: ClientType): 'default' | 'secondary' {
  switch (clientType) {
    case 'mobile':
      return 'default';
    case 'web':
      return 'secondary';
    default:
      return 'secondary';
  }
}

/**
 * Get Expiration Status
 */
function getExpirationStatus(expiresAt: string | null): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
} {
  if (!expiresAt) {
    return { label: 'No Expiry', variant: 'outline' };
  }

  const now = new Date();
  const expiry = new Date(expiresAt);
  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) {
    return { label: 'Expired', variant: 'destructive' };
  } else if (daysUntilExpiry <= 7) {
    return { label: `${daysUntilExpiry}d left`, variant: 'destructive' };
  } else if (daysUntilExpiry <= 30) {
    return { label: `${daysUntilExpiry}d left`, variant: 'secondary' };
  }
  return { label: formatDate(expiresAt), variant: 'outline' };
}

/**
 * API Keys Table
 * Renders a table of API keys with status badges, selection, and actions
 */
export function ApiKeysTable({
  apiKeys,
  isLoading,
  error,
  onRetry,
  onKeyClick,
  selectedKeys,
  onSelectionChange,
}: ApiKeysTableProps) {
  const [deleteDialogKey, setDeleteDialogKey] = useState<ApiKey | null>(null);
  const [rotateDialogKey, setRotateDialogKey] = useState<ApiKey | null>(null);
  const [rateLimitsDialogKey, setRateLimitsDialogKey] = useState<ApiKey | null>(null);

  // Handle select all
  const allSelected = apiKeys.length > 0 && selectedKeys.length === apiKeys.length;
  const someSelected = selectedKeys.length > 0 && selectedKeys.length < apiKeys.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(apiKeys.map(k => k.id));
    }
  };

  const handleSelectKey = (keyId: string) => {
    if (selectedKeys.includes(keyId)) {
      onSelectionChange(selectedKeys.filter(id => id !== keyId));
    } else {
      onSelectionChange([...selectedKeys, keyId]);
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-2">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading API keys...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Failed to load API keys</h3>
          <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
        </div>
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty State
  if (apiKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-2">
        <p className="text-sm text-muted-foreground">No API keys found</p>
        <p className="text-xs text-muted-foreground">Create a new key to get started</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  className={someSelected ? 'data-[state=checked]:bg-primary/50' : ''}
                />
              </TableHead>
              <TableHead>Key Name</TableHead>
              <TableHead>Client Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rate Limit</TableHead>
              <TableHead>Last Used</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map(apiKey => {
              const expirationStatus = getExpirationStatus(apiKey.expiresAt);
              return (
                <TableRow
                  key={apiKey.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onKeyClick(apiKey)}
                >
                  <TableCell onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedKeys.includes(apiKey.id)}
                      onCheckedChange={() => handleSelectKey(apiKey.id)}
                      aria-label={`Select ${apiKey.keyName}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{apiKey.keyName}</TableCell>
                  <TableCell>
                    <Badge variant={getClientTypeBadgeVariant(apiKey.clientType)}>
                      {apiKey.clientType.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={apiKey.isActive ? 'default' : 'secondary'}>
                      {apiKey.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {formatRateLimit(apiKey.maxRequests, apiKey.windowMs)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(apiKey.lastUsed)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={expirationStatus.variant}>{expirationStatus.label}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(apiKey.createdAt)}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onKeyClick(apiKey)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setRateLimitsDialogKey(apiKey)}>
                          <Settings2 className="h-4 w-4 mr-2" />
                          Rate Limits
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setRotateDialogKey(apiKey)}
                          disabled={!apiKey.isActive}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Rotate Key
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteDialogKey(apiKey)}
                          className="text-destructive"
                          disabled={!apiKey.isActive}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Deactivate
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Delete Dialog */}
      <ApiKeyDeleteDialog apiKey={deleteDialogKey} onClose={() => setDeleteDialogKey(null)} />

      {/* Rotate Dialog */}
      <ApiKeyRotateDialog apiKey={rotateDialogKey} onClose={() => setRotateDialogKey(null)} />

      {/* Rate Limits Dialog */}
      <ApiKeyRateLimitsDialog
        apiKey={rateLimitsDialogKey}
        onClose={() => setRateLimitsDialogKey(null)}
      />
    </>
  );
}
